import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { formatISODuration } from 'date-fns';
import { Duration } from 'moment';
import moment from 'moment-timezone';
import classnames from 'classnames';
import { isEmpty } from 'lodash';

import ArrowIcon from 'src/assets/layout/navigation/arrow.svg';
import NavigationIcon from 'src/assets/hosLogs/hosEditLog/navigation.svg';

import { Button } from 'src/components/Button';
import { StatusButton } from 'src/components/StatusButton';
import { Checkbox } from 'src/components/Checkbox';
import { Modal } from 'src/components/Modal/Modal';
import { SaveStatusPayload } from 'src/common/types';
import { getChangeDutyStatusSuccess, setChangeDutyStatus } from 'src/features/loading';
import { getIsLoading } from 'src/features/loading/loadingSelectors';

import {
  DUTY_STATUS,
  DUTY_TYPE,
  dutyStatusByEldStatus,
  ELD_DRIVER_STATUS,
} from 'src/constants/ELDConstants';
import { TIME_FORMAT } from 'src/utils/dateUtils';

import { DUTY_STATUS_ERRORS, ODOMETER_MAX, ODOMETER_MIN, TIME_INPUT_TYPE } from '../constants';
import {
  editLogStatusAction,
  getHosLogNormalized,
  getUserLocations,
  insertDutyStatusAction,
  setUnsavedHOSLogEditsPresent,
} from '../store';
import { GeoLocationDataType, UserLocationType } from '../hosLog/types';

import { useOriginEvent } from './hooks';
import {
  confirmButtonTitleByFormMode,
  createNextPeriodPayload,
  createPeriodPayload,
  DEFAULT_DATE_FORMAT,
  FORM_MODE,
  generateCompliantLocationString,
  getIsoDurationParamsByDuration,
  parseCoordinates,
  round,
} from './utils';

import './hosEditLog.scss';
import HosLogEditModal from 'src/components/HOSLogEditModal/HosLogEditModal';
import TimeInput from './TimeInput';
import { HOURS_IN_DAY } from '../../../components/LogView/utils';

interface Props {
  formMode: FORM_MODE;
  eventIndex: number;
  logStatus: number;
  geoLocationData: GeoLocationDataType | null;
  startTime: string;
  duration: number | Duration;
  location: string;
  notes: string;
  odometer: number | null;
  editedOdometer: number | null;
  onClose: () => void;
  allowOdometerEdits: boolean;
  showUnsavedDutyEditModal: boolean;
  discardDutyEdits: () => void;
  hideDutyEditWarnModal: () => void;
  destinationPage: string;
}

export const HosEditLog: React.FC<Props> = ({
  formMode,
  eventIndex,
  logStatus,
  geoLocationData,
  startTime,
  duration,
  location,
  notes,
  odometer,
  editedOdometer,
  onClose,
  allowOdometerEdits,
  showUnsavedDutyEditModal,
  discardDutyEdits,
  hideDutyEditWarnModal,
  destinationPage = '',
}) => {
  const initialLocation = location;
  const dispatch = useDispatch();
  const history = useHistory();
  const eventLogObject = useSelector(getHosLogNormalized);

  const userLocations = useSelector(getUserLocations);
  const isLoading = useSelector(getIsLoading);

  const normalizedLocations = useMemo(() => {
    return userLocations.slice(userLocations.length - 5).reverse();
  }, [userLocations]);

  const [statusView, setStatusView] = useState(logStatus);
  const [timeZone, setTimeZone] = useState('');
  const [isEndTimeTouched, setEndTimeTouched] = useState(false);

  const [offDuty, setOffDuty] = useState(logStatus === ELD_DRIVER_STATUS.OFF_PC);
  const [onDuty, setOnDuty] = useState(logStatus === ELD_DRIVER_STATUS.ON_YM);
  const [timeInputErrorPresentStart, setTimeInputErrorPresentStart] = useState<boolean>(false);
  const [timeInputErrorPresentEnd, setTimeInputErrorPresentEnd] = useState<boolean>(false);
  const [dateInputErrorPresentStart, setDateInputErrorPresentStart] = useState<boolean>(false);
  const [dateInputErrorPresentEnd, setDateInputErrorPresentEnd] = useState<boolean>(false);

  const routeMatch = useRouteMatch<{ date: string }>();
  const dateParam = routeMatch.params.date;

  useEffect(() => {
    setTimeZone(eventLogObject.timeZone);
  }, [eventLogObject]);

  const [selectedStartTime, setSelectedStartTime] = useState<any>(startTime);
  const [selectedEndTime, setSelectedEndTime] = useState<any>(
    formMode === FORM_MODE.ADD
      ? ''
      : moment(startTime)
          .add(duration, 'milliseconds')
          .format(),
  );

  const [geoLocationValue, setGeoLocationValue] = useState(
    generateCompliantLocationString(geoLocationData),
  );
  const [locationValue, setLocationValue] = useState(location);
  const [noteValue, setNoteValue] = useState(notes);

  const [errorMessage, setErrorMessage] = useState('');
  const [timeError, setTimeError] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const [noteError, setNoteError] = useState(false);
  const [odometerError, setOdometerError] = useState(false);

  const [editedStatus, setEditedStatus] = useState('');
  const [newFormatTime, setNewFormatTime] = useState(TIME_FORMAT);

  const isOdometerDisabled = odometer && odometer > 0;
  const [editedOdometerValue, setEditedOdometerValue] = useState(editedOdometer);

  const isFirstEventObject = eventIndex === 0;
  const isLastEventObject = eventLogObject?.hosGridObject?.length - 1 === eventIndex;

  const changeDutyStatusSuccess = useSelector(getChangeDutyStatusSuccess);

  const [showCannotEditLocationModal, setShowCannotEditLocationModal] = useState(false);
  const closeLocationDialogHandler = (): void => {
    setShowCannotEditLocationModal(false);
  };
  const [showCannotEditOdometerModal, setShowCannotEditOdometerModal] = useState(false);
  const closeOdometerDialogHandler = (): void => {
    setShowCannotEditOdometerModal(false);
  };

  useEffect(() => {
    if (changeDutyStatusSuccess) {
      dispatch(setChangeDutyStatus(false));
    }
  }, [changeDutyStatusSuccess, dispatch]);

  useEffect(() => {
    const shouldShowOptionalEndTime = !selectedEndTime && formMode === FORM_MODE.ADD;

    if ((shouldShowOptionalEndTime && !isEndTimeTouched) || !moment(selectedEndTime).isValid()) {
      setNewFormatTime('--:--');
    } else {
      setNewFormatTime(TIME_FORMAT);
    }
  }, [dateParam, selectedEndTime, formMode, isEndTimeTouched]);

  const isError = useMemo(() => {
    return timeError || locationError || noteError || odometerError;
  }, [timeError, locationError, noteError, odometerError]);

  const changeView = useCallback(
    view => (): void => {
      return setStatusView(view);
    },
    [],
  );

  const validateLocation = useMemo(
    () => (): void => {
      if (locationValue || geoLocationValue) {
        setLocationError(false);
      } else {
        setLocationError(true);
        setErrorMessage(DUTY_STATUS_ERRORS.LOCATION);
      }
    },
    [locationValue],
  );

  const validateNotes = useMemo(
    () => (): void => {
      if (noteValue) {
        setNoteError(false);
      } else {
        setNoteError(true);
        setErrorMessage(DUTY_STATUS_ERRORS.NOTES);
      }
    },
    [noteValue],
  );

  const [originalStartMoment, originalEndMoment, originEvent] = useOriginEvent(
    dateParam,
    eventLogObject,
    eventIndex,
  );

  const validateDates = useCallback(
    (startISO: string, endISO: string): void => {
      const start = moment(startISO);
      const end = moment(endISO);
      if (!start.isValid() || !end.isValid()) {
        setTimeError(false);
        return;
      }

      for (let i = 0; i < eventLogObject.hosGridObject.length; i++) {
        if (i === eventIndex) continue;

        if (start.isSameOrBefore(end)) {
          setTimeError(false);
        } else {
          setTimeError(true);
          setErrorMessage(DUTY_STATUS_ERRORS.START_TIME);
          break;
        }

        if (originalEndMoment && start.isAfter(originalEndMoment)) {
          setTimeError(true);
          setErrorMessage(DUTY_STATUS_ERRORS.ORIGIN_END_TIME);
          break;
        } else {
          setTimeError(false);
        }

        if (originalStartMoment && end.isBefore(originalStartMoment)) {
          setTimeError(true);
          setErrorMessage(DUTY_STATUS_ERRORS.ORIGIN_START_TIME);
          break;
        } else {
          setTimeError(false);
        }
      }
    },
    [eventLogObject.hosGridObject, eventIndex, originalEndMoment, originalStartMoment, formMode],
  );

  const validateOdometer = useCallback((odometer): void => {
    if ((odometer && odometer < ODOMETER_MIN) || (odometer && odometer > ODOMETER_MAX)) {
      setOdometerError(true);
      setErrorMessage(DUTY_STATUS_ERRORS.ODOMETER_BOUNDS);
    } else {
      setOdometerError(false);
    }
  }, []);

  const validateDrivePeriod = useCallback(
    (start: string, end: string): void => {
      const newStartMoment = moment(start);
      const newEndMoment = moment(end);
      const periodsByStatus = eventLogObject.hosGridObject.filter(
        (item: any) => item.status === ELD_DRIVER_STATUS.D,
      );

      for (let i = 0; i < periodsByStatus.length; i++) {
        if (i === eventIndex) continue;
        const startTimeByPeriod = moment(periodsByStatus[i].timeISO);
        const endTimeByPeriod = moment(startTimeByPeriod).add(
          periodsByStatus[i].duration,
          'milliseconds',
        );
        if (
          (newStartMoment.isAfter(startTimeByPeriod) && newStartMoment.isBefore(endTimeByPeriod)) ||
          (newEndMoment &&
            newEndMoment.isAfter(startTimeByPeriod) &&
            newEndMoment &&
            newEndMoment.isBefore(endTimeByPeriod)) ||
          (startTimeByPeriod.isAfter(newStartMoment) &&
            newEndMoment &&
            startTimeByPeriod.isBefore(newEndMoment))
        ) {
          setTimeError(true);
          setErrorMessage(DUTY_STATUS_ERRORS.OVERWRITTEN_DRIVE_TIME);
          break;
        } else {
          setTimeError(false);
        }
      }
    },
    [formMode, dateParam, eventLogObject, eventLogObject.hosGridObject, eventIndex],
  );

  const handleStartTimeChange = useCallback(
    (dateISO): void => {
      setSelectedStartTime(dateISO.format());
      validateDates(dateISO.format(), selectedEndTime);
      validateDrivePeriod(dateISO.format(), selectedEndTime);
    },
    [validateDates, selectedEndTime],
  );

  const handleEndTimeChange = useCallback(
    (dateISO): void => {
      setEndTimeTouched(true);
      setSelectedEndTime(dateISO.format());
      validateDates(selectedStartTime, dateISO.format());
      validateDrivePeriod(selectedStartTime, dateISO.format());
    },
    [validateDates, selectedStartTime],
  );

  const changeOdometer = useCallback(
    (event): void => {
      const odometer = parseFloat(event.currentTarget.value) || null;
      setEditedOdometerValue(odometer);
      validateOdometer(odometer);
    },
    [validateOdometer],
  );

  const changeNoteValue = useCallback((event: React.FormEvent<HTMLInputElement>) => {
    setNoteValue(event.currentTarget.value);
  }, []);

  const changeLocationValue = useCallback((event: React.FormEvent<HTMLInputElement>) => {
    setLocationValue(event.currentTarget.value);
  }, []);

  const handleOffDuty = useCallback(() => {
    setOffDuty(prev => !prev);
  }, []);

  const handleOnDuty = useCallback(() => {
    setOnDuty(prev => !prev);
  }, []);

  const saveStatusAction = useMemo(() => {
    return formMode === FORM_MODE.ADD ? insertDutyStatusAction : editLogStatusAction;
  }, [formMode]);

  const getSavePayload = useCallback(
    (payload: SaveStatusPayload) => {
      let latitude: number;
      let longitude: number;
      if (
        payload.statusPayload.location !== initialLocation &&
        payload.statusPayload.location != 'EMPTY'
      ) {
        payload.statusPayload.geoLocationData = null;
        [latitude, longitude] = parseCoordinates(formMode === FORM_MODE.ADD, locationValue);
      } else if (payload.statusPayload.geoLocationData) {
        latitude = payload.statusPayload.geoLocationData?.lat || -1;
        longitude = payload.statusPayload.geoLocationData?.lng || -1;
      } else {
        [latitude, longitude] = parseCoordinates(formMode === FORM_MODE.ADD, locationValue);
      }

      return formMode === FORM_MODE.EDIT
        ? payload
        : {
            ...payload,
            statusPayload: {
              ...payload.statusPayload,
              latitude,
              longitude,
              enableReverseGeocode: false,
            },
          };
    },
    [locationValue, formMode],
  );

  useEffect(() => {
    const initialEndTime = moment(startTime).add(duration, 'milliseconds');
    const startTimeVal = selectedStartTime;
    const endTimeVal = selectedEndTime;
    const currentOffDuty = logStatus === ELD_DRIVER_STATUS.OFF_PC;
    const currentOnDuty = logStatus === ELD_DRIVER_STATUS.ON_YM;
    const initialStartTime = moment(startTime);

    let unsavedChangesPresent = false;
    unsavedChangesPresent =
      logStatus != statusView ||
      initialStartTime.format() != moment(startTimeVal).format() ||
      initialEndTime.format() != moment(endTimeVal).format() ||
      location != locationValue ||
      notes != noteValue ||
      editedOdometer != editedOdometerValue ||
      offDuty != currentOffDuty ||
      onDuty != currentOnDuty;

    dispatch(setUnsavedHOSLogEditsPresent(unsavedChangesPresent));
  }, [
    selectedStartTime,
    selectedEndTime,
    offDuty,
    onDuty,
    editedOdometerValue,
    locationValue,
    noteValue,
  ]);

  const confirmDutyStatusChange = () => {
    let selectedStartTimeMoment = moment(selectedStartTime);
    let selectedEndTimeMoment = moment(selectedEndTime);
    let timestamp = selectedStartTimeMoment.format();

    const previousEvent = eventLogObject.hosGridObject[eventIndex - 1];
    const nextEvent = eventLogObject.hosGridObject[eventIndex + 1];

    let modifiedOriginLogPayload: SaveStatusPayload | undefined;
    let nextPeriodPayload: SaveStatusPayload | undefined;
    let previousEventPayload: SaveStatusPayload | undefined;
    let payloadStatus = editedStatus;

    if (originEvent) {
      const originEndTime = moment(originEvent.timeISO).add(originEvent.duration, 'milliseconds');
      if (!selectedEndTimeMoment.isValid()) {
        selectedEndTimeMoment = originEndTime.clone();
      }

      if (!selectedStartTimeMoment.isValid()) {
        selectedStartTimeMoment = moment(originEvent.timeISO).clone();
      }

      const originStartTime = moment(originEvent.timeISO);

      if (
        selectedStartTimeMoment.isAfter(moment(originStartTime)) &&
        selectedEndTimeMoment.isBefore(moment(originEndTime))
      ) {
        const modifiedOriginTimeStamp = selectedStartTimeMoment.format();

        const durationBetweenStartAndEnd = moment.duration(
          selectedEndTimeMoment.diff(selectedStartTimeMoment, 'minutes'),
          'minutes',
        );

        const [days, hours, minutes, seconds] = getIsoDurationParamsByDuration(
          durationBetweenStartAndEnd,
        );

        const modifiedDuration = formatISODuration({
          days,
          hours,
          minutes,
          seconds,
        }).replace('0Y0M', '');

        modifiedOriginLogPayload = createPeriodPayload(
          eventLogObject,
          modifiedOriginTimeStamp,
          modifiedDuration,
          originEvent.location,
          originEvent.geoLocationData || null,
          originEvent.notes,
          dutyStatusByEldStatus[statusView as ELD_DRIVER_STATUS],
          originEvent.odometer,
          originEvent.editedOdometer,
        );
      }

      if (selectedStartTimeMoment.isAfter(moment(originStartTime))) {
        const previousEventTimestamp = moment(originEvent.timeISO).format();
        const duration = moment.duration(
          selectedStartTimeMoment.diff(moment(originStartTime), 'minutes'),
          'minutes',
        );
        const [days, hours, minutes, seconds] = getIsoDurationParamsByDuration(duration);

        const previousDuration = formatISODuration({
          days,
          hours,
          minutes,
          seconds,
        }).replace('0Y0M', '');

        previousEventPayload = createPeriodPayload(
          eventLogObject,
          previousEventTimestamp,
          previousDuration,
          '',
          null,
          '',
          dutyStatusByEldStatus[previousEvent.status as ELD_DRIVER_STATUS],
          previousEvent.odometer,
          previousEvent.editedOdometer,
        );
      }

      if (selectedEndTimeMoment.isBefore(moment(originEndTime))) {
        const nextPeriodTimeStamp = moment(nextEvent.timeISO).format();
        const nextPeriodDuration = 'PT0H0M0S';

        nextPeriodPayload = createPeriodPayload(
          eventLogObject,
          nextPeriodTimeStamp,
          nextPeriodDuration,
          originEvent.location,
          originEvent.geoLocationData || null,
          originEvent.notes,
          dutyStatusByEldStatus[nextEvent.status as ELD_DRIVER_STATUS],
          originEvent.odometer,
          originEvent.editedOdometer,
        );

        timestamp = selectedEndTimeMoment.format();

        payloadStatus = dutyStatusByEldStatus[nextEvent?.status as ELD_DRIVER_STATUS];
      }

      if (selectedEndTimeMoment.isAfter(moment(originEndTime))) {
        nextPeriodPayload = createNextPeriodPayload(
          eventLogObject,
          selectedStartTimeMoment,
          selectedEndTimeMoment,
          eventLogObject.timeZone,
          formMode,
        );
      }
    } else {
      // Inserting a new duty status
      if (!moment(selectedStartTimeMoment).isValid()) {
        selectedStartTimeMoment = moment(eventLogObject.logStartTimeISO);
        timestamp = selectedStartTimeMoment.format();
        setNewFormatTime('--:--');
      }
      if (!moment(selectedEndTimeMoment).isValid()) {
        selectedStartTimeMoment = moment(eventLogObject.logStartTimeISO);
      }

      nextPeriodPayload = createNextPeriodPayload(
        eventLogObject,
        selectedStartTimeMoment,
        selectedEndTimeMoment,
        eventLogObject.timeZone,
        formMode,
      );
    }

    let duration;

    if (originalEndMoment && selectedEndTimeMoment.isBefore(moment(originalEndMoment))) {
      duration = moment.duration(
        originalEndMoment.diff(selectedEndTimeMoment, 'minutes'),
        'minutes',
      );
    } else {
      duration = moment.duration(
        selectedEndTimeMoment.diff(selectedStartTimeMoment, 'minutes'),
        'minutes',
      );
    }

    const [days, hours, minutes, seconds] = getIsoDurationParamsByDuration(duration);
    const durationByDevice = newFormatTime === '--:--';
    const newDuration = durationByDevice
      ? 'PT0H0M0S'
      : formatISODuration({
          days,
          hours,
          minutes,
          seconds,
        }).replace('0Y0M', '');
    discardDutyEdits();
    dispatch(
      saveStatusAction(
        getSavePayload({
          ...createPeriodPayload(
            eventLogObject,
            timestamp,
            newDuration,
            locationValue,
            geoLocationData,
            noteValue,
            payloadStatus as DUTY_STATUS,
            odometer,
            editedOdometerValue,
          ),
          modifiedOriginLogPayload,
          nextPeriodPayload,
          previousEventPayload,
        }),
      ),
    );
  };

  const modalStateHandler = useCallback(() => {
    if (allowOdometerEdits) {
      validateOdometer(editedOdometerValue);
    }
    validateNotes();
    validateLocation();

    if (
      (locationValue || geoLocationValue) &&
      noteValue &&
      !timeError &&
      (allowOdometerEdits ? !odometerError : true)
    ) {
      confirmDutyStatusChange();
    }
  }, [
    locationValue,
    noteValue,
    editedOdometerValue,
    validateLocation,
    validateOdometer,
    validateNotes,
    editedStatus,
    timeError,
    odometerError,
    selectedStartTime,
    selectedEndTime,
  ]);

  const actionButtonTitle = isLoading
    ? confirmButtonTitleByFormMode[FORM_MODE.LOADING]
    : confirmButtonTitleByFormMode[formMode];

  const offStatus = statusView === ELD_DRIVER_STATUS.OFF || statusView === ELD_DRIVER_STATUS.OFF_PC;
  const onStatus = statusView === ELD_DRIVER_STATUS.ON || statusView === ELD_DRIVER_STATUS.ON_YM;

  const renderOffContent = (): JSX.Element | null => {
    return (
      (offStatus && (
        <div className="hos-edit-log__checkbox-content">
          <Checkbox
            headerPosition="start"
            title="Personal Conveyance"
            name="yardMoves"
            checked={offDuty}
            onChange={handleOffDuty}
          />
        </div>
      )) ||
      null
    );
  };

  const renderOnContent = (): JSX.Element | null => {
    return (
      (onStatus && (
        <div className="hos-edit-log__checkbox-content">
          <Checkbox
            headerPosition="start"
            title="Yard Moves"
            name="conveyance"
            checked={onDuty}
            onChange={handleOnDuty}
          />
        </div>
      )) ||
      null
    );
  };

  const insertLocationValue = useCallback(
    (item: UserLocationType): (() => void) => (): void =>
      setLocationValue(`${item.city}, ${item.state}`),
    [],
  );

  useEffect(() => {
    if (offStatus) {
      setEditedStatus(offDuty ? DUTY_STATUS.OFF_DUTY_PERSONAL_CONVEYANCE : DUTY_STATUS.OFF_DUTY);
    } else if (statusView === ELD_DRIVER_STATUS.SB) {
      setEditedStatus(DUTY_STATUS.SLEEPER_BERTH);
    } else if (onStatus) {
      setEditedStatus(onDuty ? DUTY_STATUS.ON_DUTY_YARD_MOVE : DUTY_STATUS.ON_DUTY_NOT_DRIVING);
    }
  }, [statusView, offDuty, onDuty, offStatus, onStatus]);

  const headerStyle = classnames('hos-edit-log__header', {
    'hos-edit-log__header-desktop': !isMobile,
  });

  const buttonBackStyle = classnames('button-back', {
    'button-back-mobile': isMobile,
  });

  const logSettingsStyle = classnames('hos-edit-log__log-settings', {
    'hos-edit-log__log-settings-mobile': isMobile,
  });

  const tabStatusStyle = classnames('hos-edit-log__tab-status', {
    'hos-edit-log__tab-status-desktop': !isMobile,
  });

  const dutyStatusStyle = classnames('hos-edit-log__duty-status', {
    'hos-edit-log__duty-status-desktop': !isMobile,
  });

  const locationItemStyle = classnames('hos-edit-log__info-item', {
    'hos-edit-log__info-item-error': locationError,
  });

  const locationTitleStyle = classnames('hos-edit-log__info-title', {
    'hos-edit-log__info-title-error': locationError,
    'red-title-color': true,
  });

  const noteItemStyle = classnames('hos-edit-log__info-item', {
    'hos-edit-log__info-item-error': noteError,
  });

  const noteTitleStyle = classnames('hos-edit-log__info-title', {
    'hos-edit-log__info-title-error': noteError,
    'red-title-color': true,
  });

  const odometerItemStyle = classnames('hos-edit-log__info-item', {
    'hos-edit-log__info-item-error': odometerError,
  });

  const odometerTitleStyle = classnames('hos-edit-log__info-title', {
    'hos-edit-log__info-title-error': odometerError,
  });

  const buttonDutyStatusStyle = classnames('hos-edit-log__status-button', {
    'hos-edit-log__status-button__secondary': isLoading,
  });

  const endTimeLabel = `End Time ${formMode === FORM_MODE.ADD ? '(optional)' : ''}`;

  const CannotEditLocationModal = () => {
    return (
      <Modal
        icon={'icon i-warning'}
        header={DUTY_STATUS_ERRORS.CANNOT_EDIT_LOCATION}
        description={DUTY_STATUS_ERRORS.LOCATION_WARNING}
        confirmButton={'OK'}
        onConfirmFunction={closeLocationDialogHandler}
        onCloseDialog={closeLocationDialogHandler}
        showCancelButton={false}
      />
    );
  };

  const CannotEditOdometerModal = () => {
    return (
      <Modal
        icon={'icon i-warning'}
        header={DUTY_STATUS_ERRORS.CANNOT_EDIT_ODOMETER}
        description={DUTY_STATUS_ERRORS.ODOMETER_WARNING}
        confirmButton={'OK'}
        onConfirmFunction={closeOdometerDialogHandler}
        onCloseDialog={closeOdometerDialogHandler}
        showCancelButton={false}
      />
    );
  };

  const is36HoursLog = Boolean(
    eventLogObject?.logLengthHours && eventLogObject.logLengthHours > HOURS_IN_DAY + 1,
  );

  return (
    <React.Fragment>
      {showUnsavedDutyEditModal && (
        <HosLogEditModal
          title="Leave Page"
          message="All changes will be lost. Do you want to proceed?"
          proceedButtonText="Discard Changes"
          proceedButtonFn={() => {
            discardDutyEdits();
            destinationPage.length > 0 && history.push(destinationPage);
          }}
          undoButtonText="Cancel"
          undoButtonFn={hideDutyEditWarnModal}
        />
      )}
      {showCannotEditLocationModal && <CannotEditLocationModal />}
      {showCannotEditOdometerModal && <CannotEditOdometerModal />}
      <div className="hos-edit-log">
        <div className={headerStyle}>
          <Button title="Back" onClick={onClose} icon={ArrowIcon} className={buttonBackStyle} />
          Duty Status
        </div>

        <div className={tabStatusStyle}>
          {isError && (
            <div className="hos-edit-log__show-error">
              <div className="hos-edit-log__error-title">{errorMessage}</div>
            </div>
          )}
          <div className="hos-edit-log__tab-status-btn">
            <StatusButton
              active={
                statusView === ELD_DRIVER_STATUS.OFF || statusView === ELD_DRIVER_STATUS.OFF_PC
              }
              onClick={changeView(ELD_DRIVER_STATUS.OFF)}
              dutyType={DUTY_TYPE.OFF}
              dutySubtitle="Off Duty"
            />
            <StatusButton
              active={statusView === ELD_DRIVER_STATUS.SB}
              onClick={changeView(ELD_DRIVER_STATUS.SB)}
              dutyType={DUTY_TYPE.SB}
              dutySubtitle="Sleeper Berth"
            />
            <StatusButton
              active={statusView === ELD_DRIVER_STATUS.ON || statusView === ELD_DRIVER_STATUS.ON_YM}
              onClick={changeView(ELD_DRIVER_STATUS.ON)}
              dutyType={DUTY_TYPE.ON}
              dutySubtitle="On Duty"
            />
          </div>
        </div>
        <div className="hos-edit-log__checkbox">
          {renderOffContent()}
          {renderOnContent()}
        </div>
        <div className={logSettingsStyle}>
          <form className="hos-edit-log__textfield-container" noValidate>
            <div className={`start-end-time-input-row ${isMobile ? 'mobile-view' : ''}`}>
              <TimeInput
                timezoneText={timeZone}
                label="Start Time (required)"
                value={startTime}
                setTimeValue={handleStartTimeChange}
                isDisabled={isFirstEventObject}
                formMode={formMode}
                timeType={TIME_INPUT_TYPE.START_TIME}
                isRequired={true}
                setTimeInputError={setTimeInputErrorPresentStart}
                setDateInputError={setDateInputErrorPresentStart}
                is36HoursMode={is36HoursLog}
                startDateLog={eventLogObject.logDate}
                endDateLog={moment(eventLogObject.logDate, DEFAULT_DATE_FORMAT)
                  .add(1, 'days')
                  .format(DEFAULT_DATE_FORMAT)}
                dayStartHour={eventLogObject.logDayStartTime}
              />
              <TimeInput
                timezoneText={timeZone}
                label={endTimeLabel}
                value={
                  formMode !== FORM_MODE.ADD
                    ? moment(startTime)
                        .add(duration, 'milliseconds')
                        .toISOString()
                    : ''
                }
                setTimeValue={handleEndTimeChange}
                isDisabled={isLastEventObject}
                formMode={formMode}
                timeType={TIME_INPUT_TYPE.END_TIME}
                isRequired={false}
                setTimeInputError={setTimeInputErrorPresentEnd}
                setDateInputError={setDateInputErrorPresentEnd}
                is36HoursMode={is36HoursLog}
                dayStartHour={eventLogObject.logDayStartTime}
                startDateLog={moment(eventLogObject.logDate, DEFAULT_DATE_FORMAT).format(
                  DEFAULT_DATE_FORMAT,
                )}
                endDateLog={moment(eventLogObject.logDate, DEFAULT_DATE_FORMAT)
                  .add(1, 'days')
                  .format(DEFAULT_DATE_FORMAT)}
              />
            </div>
          </form>
          <div className={locationItemStyle}>
            <div className={locationTitleStyle}>Location (required)</div>
            <div className="hos-edit-log__info-subtitle">
              {geoLocationValue ? (
                <div
                  className="hos-edit-log__info-input"
                  onClick={() => {
                    setShowCannotEditLocationModal(true);
                  }}
                >
                  {geoLocationValue}
                </div>
              ) : (
                <input
                  value={locationValue}
                  className="hos-edit-log__info-input"
                  onChange={changeLocationValue}
                />
              )}
              {isMobile && (
                <img
                  src={NavigationIcon}
                  alt="NavigationIcon"
                  className="hos-edit-log__navigation-icon"
                />
              )}
            </div>
          </div>
          {!isMobile && !geoLocationValue && (
            <div className="hos-edit-log__location-subtitle">
              {!isEmpty(normalizedLocations) && 'Location noted in this log:'}
              {normalizedLocations.map(item => {
                return (
                  <div
                    key={item.speed}
                    className="hos-edit-log__location-item"
                    onClick={insertLocationValue(item)}
                  >
                    {item.city}, {item.state}
                  </div>
                );
              })}
            </div>
          )}

          <div className={noteItemStyle}>
            <div className={noteTitleStyle}>Notes (required)</div>
            <div className="hos-edit-log__info-subtitle">
              <input
                value={noteValue}
                className="hos-edit-log__info-input"
                onChange={changeNoteValue}
              />
            </div>
          </div>
          {allowOdometerEdits && formMode == FORM_MODE.EDIT && (
            <div className={odometerItemStyle}>
              <div className={odometerTitleStyle}>Odometer (mi)</div>
              <div className="hos-edit-log__info-subtitle">
                {isOdometerDisabled ? (
                  <div
                    className="hos-edit-log__info-input"
                    onClick={() => {
                      setShowCannotEditOdometerModal(true);
                    }}
                  >
                    {round(odometer) || ''}
                  </div>
                ) : (
                  <input
                    id="odometerStart"
                    type="number"
                    defaultValue={round(editedOdometerValue) || ''}
                    onChange={changeOdometer}
                    className="hos-edit-log__info-input"
                    autoComplete="off"
                  />
                )}
              </div>
            </div>
          )}
        </div>
        <div className={dutyStatusStyle}>
          <Button
            className={buttonDutyStatusStyle}
            onClick={modalStateHandler}
            title={actionButtonTitle}
            disabled={
              statusView === ELD_DRIVER_STATUS.EMPTY ||
              isLoading ||
              timeInputErrorPresentStart ||
              timeInputErrorPresentEnd ||
              dateInputErrorPresentStart ||
              dateInputErrorPresentEnd
            }
          />
        </div>
      </div>
    </React.Fragment>
  );
};
